import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ButtonDirective } from '@coreui/angular-pro';
import { MatomoTrackerService } from 'analytics';
import { I18nService, TranslatePipe } from 'i18n';

import { FunnelService, QuizProduct, QuizStatus } from '../../../services/funnel.service';
import { FunnelShellComponent } from '../funnel-shell/funnel-shell.component';

const MATOMO_CATEGORY = 'FAC3LESS Funnel';

/**
 * Recommendation page — shown after the quiz completes. Loads the session,
 * looks up the recommended product, and renders the card-specific marketing
 * block with the "Jetzt kaufen" CTA. As long as nothing was purchased the
 * user can still step back into the quiz and change the last answer.
 */
@Component({
  selector: 'app-funnel-result-page',
  standalone: true,
  imports: [CommonModule, ButtonDirective, TranslatePipe, FunnelShellComponent],
  templateUrl: './funnel-result-page.component.html',
  styleUrls: ['./funnel-result-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FunnelResultPageComponent {
  private router     = inject(Router);
  private funnel     = inject(FunnelService);
  private i18n       = inject(I18nService);
  private matomo     = inject(MatomoTrackerService);
  private destroyRef = inject(DestroyRef);

  loading = signal(true);
  busy    = signal(false);
  error   = signal<string | null>(null);
  product = signal<QuizProduct | null>(null);
  code    = signal<string | null>(null);
  status  = signal<QuizStatus | null>(null);

  // "Set a password / log in first" — emails the login link without buying.
  pwBusy  = signal(false);
  pwSent  = signal<string | null>(null);   // holds the email it was sent to
  pwError = signal(false);

  // i18n key for the marketing copy block, e.g. "funnel.result.card_silver"
  resultKey = computed(() => {
    const c = this.code();
    if (!c) return null;
    return `funnel.result.${c.toLowerCase()}`;
  });

  // Changing answers only makes sense before a purchase exists.
  canGoBack = computed(() => this.status() === 'completed');

  constructor() {
    // No local-token guard: the backend can resume via the `cid` auth cookie
    // and re-issues a session token; only a 401/404 sends the user back to /r.
    this.funnel.showQuiz()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => {
          this.loading.set(false);
          if (session.session_token) this.funnel.storeSessionToken(session.session_token);
          if (session.status !== 'completed' && session.status !== 'purchased') {
            // Quiz not done yet — go back to wizard.
            const lang = this.i18n.getLanguage() || 'en';
            this.router.navigate(['/', lang, 'quiz'], { replaceUrl: true });
            return;
          }
          this.status.set(session.status);
          this.code.set(session.recommended_product_code || null);
          this.product.set(session.product || null);
        },
        error: err => {
          this.loading.set(false);
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not load your recommendation.');
        }
      });
  }

  onCheckout(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'quiz', 'checkout']);
  }

  /**
   * Email the user a password-setup / login link instead of buying right now.
   * They stay on this page (can still buy later) and just get a confirmation.
   */
  onSetPassword(): void {
    if (this.pwBusy()) return;

    this.pwBusy.set(true);
    this.pwError.set(false);

    this.funnel.requestPasswordSetup()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          this.pwBusy.set(false);
          this.pwSent.set(res.email || '');
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'password_setup_requested',
            name:     'from_result'
          });
        },
        error: () => {
          this.pwBusy.set(false);
          this.pwError.set(true);
        }
      });
  }

  /** Undo the final answer on the server and drop back into the quiz wizard. */
  onBackToQuiz(): void {
    if (this.busy() || !this.canGoBack()) return;

    this.busy.set(true);
    this.error.set(null);

    // step 0 = server default: rewind to the last answered question.
    this.funnel.rewindQuiz(0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.busy.set(false);
          this.matomo.trackEvent({
            category: MATOMO_CATEGORY,
            action:   'quiz_rewound',
            name:     'from_result'
          });
          const lang = this.i18n.getLanguage() || 'en';
          this.router.navigate(['/', lang, 'quiz'], { replaceUrl: true });
        },
        error: err => {
          this.busy.set(false);
          if (err?.status === 401 || err?.status === 404) {
            this.funnel.clearSessionToken();
            this.redirectToLanding();
            return;
          }
          this.error.set(err?.error?.error || 'Could not go back. Please try again.');
        }
      });
  }

  private redirectToLanding(): void {
    const lang = this.i18n.getLanguage() || 'en';
    this.router.navigate(['/', lang, 'r'], { replaceUrl: true });
  }
}
